/**
 * NotificationBell — calm in-app notification surface in the app header.
 *
 *  - Polls `/api/notifications/unread-count` every 60s for the badge.
 *  - On click, opens a dropdown that fetches `/api/notifications` (top 50).
 *  - Each row: severity dot, title, short message, relative timestamp,
 *    optional action link, dismiss button.
 *  - "Mark all read" footer link.
 *
 * No pulsing alarm behaviour, no aggressive animation. Backend is
 * authoritative on visibility — this component renders whatever the API
 * returns, with a 401/403 safety: if the calling user isn't permitted
 * we silently render nothing.
 */
import React, { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000;

const SEVERITY_TONE = {
  info:     { dot: '#0284c7', label: 'Info' },
  warning:  { dot: '#b45309', label: 'Warning' },
  critical: { dot: '#b91c1c', label: 'Critical' },
};

function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (Number.isNaN(diff)) return '';
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationBell({ onAction }) {
  const [open, setOpen] = useState(false);
  const [allowed, setAllowed] = useState(true);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchUnread = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/unread-count`,
        { credentials: 'include' });
      if (res.status === 401 || res.status === 403) { setAllowed(false); return; }
      if (!res.ok) return;
      const body = await res.json();
      setUnread(body.unread || 0);
      setAllowed(true);
    } catch {
      /* fail-quiet */
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications?limit=50`,
        { credentials: 'include' });
      if (!res.ok) { setItems([]); return; }
      const body = await res.json();
      setItems(body.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, POLL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) fetchList();
  }, [open]);

  // Dismiss panel on outside-click
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!allowed) return null;

  const onMarkRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/notifications/${id}/read`,
        { method: 'POST', credentials: 'include' });
      setItems((xs) => xs.map((i) => (i.id === id ? { ...i, status: 'read' } : i)));
      setUnread((n) => Math.max(0, n - 1));
    } catch { /* */ }
  };

  const onDismiss = async (id) => {
    try {
      await fetch(`${API_URL}/api/notifications/${id}/dismiss`,
        { method: 'POST', credentials: 'include' });
      const removed = items.find((i) => i.id === id);
      setItems((xs) => xs.filter((i) => i.id !== id));
      if (removed && removed.status === 'unread') setUnread((n) => Math.max(0, n - 1));
    } catch { /* */ }
  };

  const onReadAll = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all`,
        { method: 'POST', credentials: 'include' });
      setItems((xs) => xs.map((i) => ({ ...i, status: i.status === 'unread' ? 'read' : i.status })));
      setUnread(0);
    } catch { /* */ }
  };

  const onActionClick = async (item) => {
    if (item.status === 'unread') await onMarkRead(item.id);
    if (typeof onAction === 'function' && item.action_path) {
      onAction(item.action_path);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }} data-testid="notification-bell">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications (${unread} unread)`}
        title="Notifications"
        style={{
          position: 'relative',
          padding: '6px 10px',
          background: 'transparent',
          border: '1px solid var(--border-subtle, #e2e8f0)',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 13,
          color: 'var(--text-primary, #0f172a)',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
        data-testid="notification-bell-button"
      >
        <span aria-hidden style={{ fontSize: 14 }}>🔔</span>
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            style={{
              minWidth: 18, height: 18, padding: '0 5px',
              background: '#b91c1c', color: '#fff',
              borderRadius: 9, fontSize: 11, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="qf-glass-panel qf-menu-open"
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)',
            width: 380, maxHeight: 480, overflow: 'auto',
            borderRadius: 10,
            zIndex: 40,
          }}
        >
          <div style={{
            padding: '10px 14px', borderBottom: '1px solid var(--border-subtle, #eee)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <strong style={{ fontSize: 13, fontWeight: 600 }}>Notifications</strong>
            {items.some((i) => i.status === 'unread') && (
              <button
                type="button"
                onClick={onReadAll}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 11, color: 'var(--text-secondary, #64748b)',
                  textDecoration: 'underline',
                }}
                data-testid="notification-read-all"
              >
                Mark all read
              </button>
            )}
          </div>

          {loading && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-secondary, #64748b)' }}>
              Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div
              data-testid="notification-panel-empty"
              style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--text-secondary, #64748b)' }}
            >
              You're all caught up.
            </div>
          )}

          {!loading && items.map((it) => {
            const tone = SEVERITY_TONE[it.severity] || SEVERITY_TONE.info;
            const unreadRow = it.status === 'unread';
            return (
              <div
                key={it.id}
                data-testid={`notification-item-${it.event_type}`}
                style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--border-subtle, #f1f5f9)',
                  background: unreadRow ? 'var(--bg-subtle, #f8fafc)' : 'transparent',
                  display: 'grid', gridTemplateColumns: '12px 1fr auto', gap: 8,
                  alignItems: 'flex-start',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 8, height: 8, borderRadius: 4,
                    background: tone.dot, marginTop: 6,
                  }}
                  title={tone.label}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{it.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary, #475569)', marginTop: 2 }}>
                    {it.message}
                  </div>
                  <div style={{
                    display: 'flex', gap: 10, alignItems: 'center', marginTop: 6,
                    fontSize: 11, color: 'var(--text-secondary, #64748b)',
                  }}>
                    <span>{relativeTime(it.created_at)}</span>
                    {it.action_label && it.action_path && (
                      <button
                        type="button"
                        onClick={() => onActionClick(it)}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          padding: 0, color: '#0284c7', textDecoration: 'underline',
                          fontSize: 11,
                        }}
                        data-testid={`notification-action-${it.event_type}`}
                      >
                        {it.action_label}
                      </button>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onDismiss(it.id)}
                  aria-label="Dismiss"
                  title="Dismiss"
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--text-secondary, #64748b)', fontSize: 14,
                    padding: 0, lineHeight: 1,
                  }}
                  data-testid="notification-dismiss-btn"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
